import * as helpers from "./helpers";
import "./tutorialStyle.css";

const DEFAULT_TIMEOUT = 6000;
const HIGHLIGHT_PADDING = 14;
const TUTORIAL_FRAME_SELECTOR =
  "#tutorial-overlay, .tutorial-overlay, .tutorial-highlight, .tutorial-card";
const TUTORIAL_DOM_SELECTOR = `#tutorial-overlay-block, ${TUTORIAL_FRAME_SELECTOR}`;
const CADASTRO_BUTTON_SELECTOR_ALIASES = [
  "#btn--novoUsuario",
  "#btn--novaPessoa",
  "#btn--novoPeriodo",
  "#btn--novoAfastamento",
  "#btn--novoPostoTrabalho",
  "#btn--cadastrarCliente",
  "#btn--novoContrato",
  "#btn--novoCFOP",
  "#btn--novoCFOPMun",
  "#btn--novoCFOPest",
  "#btn--novoEscopo",
  "#btn--novaParametrizacao",
  "#btn--cadastroContaBancaria",
  "#btn--cadastroCentroCusto",
  "#btn--cadastroContaFinanceira",
  "#btn--cadastroFornecedor",
  "#btn--cadastroTituloReceber",
  "#btn--cadastroTituloPagar",
  "#btn--cadastroRegistroRecebimento",
  "#btn--cadastroRegistroPagamento",
  "#btn--cadastroTransferencia",
  "#btn--novoCargo",
  "#btn--novoConvenio",
  "#btn--novoOptante",
  "#btn--cadastrarPedido",
  "#btn--metodoCobranca",
];
const SELECTOR_ALIASES = CADASTRO_BUTTON_SELECTOR_ALIASES.reduce(
  (aliases, selector) => ({
    ...aliases,
    [selector]: ["#btn--cadastro"],
  }),
  {
    "#btn--novaMovimentacao": ["#btn--cadastro", "#btn--cadastroMovimentacao"],
    "#procurarProduto": ['input[name="searchProdutos"]'],
    "#procurarImob": ['input[name="searchProdutos"]'],
  }
);

let activeSession = null;
let releaseGeneration = 0;

export async function startTutorial(rawSteps = [], options = {}) {
  const steps = prepareRuntimeSteps(normalizeSteps(rawSteps), options.router);
  if (!steps.length) return;

  stopTutorial();

  const session = {
    aborted: false,
    options,
    cleanup: [],
  };
  activeSession = session;
  createBlockOverlay();

  try {
    let index = 0;
    while (index < steps.length && !session.aborted) {
      const expandedSteps = expandMenuNavigationStep(
        steps[index],
        session.options.router
      );
      if (expandedSteps.length) {
        steps.splice(index, 1, ...expandedSteps);
      }

      const display = resolveStepDisplayPosition(steps, index);
      const action = await renderStep(
        steps[index],
        display.index,
        display.total,
        session
      );

      if (action === "back") {
        index = previousVisibleStepIndex(steps, index);
      } else if (action === "retry") {
        continue;
      } else if (action === "abort") {
        session.aborted = true;
      } else {
        index += 1;
      }
    }
  } finally {
    runCleanup(session);
    releaseTutorialDom();
    if (activeSession === session) {
      activeSession = null;
    }
  }
}

export function stopTutorial() {
  if (activeSession) {
    activeSession.aborted = true;
    runCleanup(activeSession);
  }
  releaseTutorialDom();
  activeSession = null;
}

export async function dispatchAction(action, payload, origin) {
  const overlay = document.getElementById("tutorial-overlay");
  if (!overlay) return;
  overlay.dispatchEvent(
    new CustomEvent("tutorialAction", {
      detail: { action, payload, origin },
    })
  );
}

async function renderStep(step, index, total, session) {
  clearFrame();
  runCleanup(session);

  const menuNavigationAction = await renderMenuNavigationStep(
    step,
    index,
    total,
    session
  );
  if (menuNavigationAction) {
    return menuNavigationAction;
  }

  await navigateIfNeeded(step, session.options.router);

  if (session.aborted) return "abort";

  const selectorList = getStepSelectors(step);

  if (step.navigationOnly === true && selectorList.length === 0) {
    await awaitTimeout(step.afterNavigationDelayMs || 160);
    return "next";
  }

  const hasSelector = selectorList.length > 0;
  const type = step.type || (hasSelector ? "text" : "textOnly");
  const target =
    hasSelector && type !== "textOnly"
      ? await getTarget(selectorList, step.timeoutMs).catch(() => null)
      : null;

  if (hasSelector && !target && !step.optional) {
    return showMissingTargetStep(step, index, total, session);
  }

  if (target && step.scrollIntoView !== false) {
    await scrollIntoView(target);
  }

  return renderVisibleStep(
    step,
    index,
    total,
    session,
    target,
    type,
    hasSelector
  );
}

function renderVisibleStep(
  step,
  index,
  total,
  session,
  target,
  type,
  canRetry
) {
  const selectorList = getStepSelectors(step);
  const overlay = document.createElement("div");
  overlay.id = "tutorial-overlay";
  overlay.className = target
    ? "tutorial-overlay tutorial-overlay--target"
    : "tutorial-overlay tutorial-overlay--text";

  const block = ensureBlockOverlay();
  block.appendChild(overlay);

  if (target) {
    renderHighlight(overlay, target, step, session);
  }

  const card = createInfoBox(step, {
    index,
    total,
    target,
    type,
    canRetry,
    isLast: index === total - 1,
  });

  overlay.appendChild(card);
  positionInfoBox(card, target);

  if (target && selectorList.length > 0) {
    const interval = setInterval(() => {
      const currentTarget = findTarget(selectorList);
      if (
        currentTarget instanceof HTMLElement &&
        currentTarget.offsetParent !== null
      ) {
        updateHighlight(overlay, currentTarget);
        positionInfoBox(card, currentTarget);
      }
    }, 250);
    session.cleanup.push(() => clearInterval(interval));
  }

  return waitForStepAction(card, step, target, type, {
    isLast: index === total - 1,
    session,
    router: session.options.router,
  });
}

function showMissingTargetStep(step, index, total, session) {
  const fallback = {
    type: "textOnly",
    boxTitle: step.boxTitle || "Elemento nao encontrado",
    boxText:
      "Nao encontramos o ponto esperado desta etapa. A tela pode ter mudado. Voce pode tentar novamente, pular esta etapa ou encerrar o tutorial.",
    selector: step.selector,
    selectors: getStepSelectors(step),
  };
  const block = ensureBlockOverlay();
  const overlay = document.createElement("div");
  overlay.id = "tutorial-overlay";
  overlay.className = "tutorial-overlay tutorial-overlay--text";
  block.appendChild(overlay);

  const card = createInfoBox(fallback, {
    index,
    total,
    canRetry: true,
    missingTarget: true,
    isLast: index === total - 1,
  });
  overlay.appendChild(card);
  return waitForStepAction(card, fallback, null, "textOnly", {
    isLast: index === total - 1,
    session,
    router: session.options.router,
  });
}

function createInfoBox(step, context) {
  const card = document.createElement("section");
  card.className = "tutorial-card";
  card.setAttribute("role", "dialog");
  card.setAttribute("aria-modal", "true");
  keepPageFocus(card);

  const eyebrow = document.createElement("div");
  eyebrow.className = "tutorial-card__eyebrow";
  eyebrow.textContent = `${context.index + 1} de ${context.total}`;
  card.appendChild(eyebrow);

  const title = document.createElement("h3");
  title.className = "tutorial-card__title";
  title.textContent = step.boxTitle || step.title || "Tutorial";
  card.appendChild(title);

  const text = document.createElement("div");
  text.className = "tutorial-card__text";
  text.innerHTML = sanitizeHtml(step.boxText || step.text || "");
  card.appendChild(text);

  const actions = document.createElement("div");
  actions.className = "tutorial-card__actions";

  const exit = createButton(
    "Sair",
    "tutorial-card__button tutorial-card__button--ghost",
    "abort"
  );
  const back = createButton(
    "Voltar",
    "tutorial-card__button tutorial-card__button--ghost",
    "back"
  );
  back.disabled = context.index === 0;

  const retry = createButton(
    "Tentar de novo",
    "tutorial-card__button tutorial-card__button--ghost",
    "retry"
  );
  retry.hidden = !context.canRetry;

  const nextLabel = primaryLabel(step, context);
  const next = createButton(
    nextLabel,
    "tutorial-card__button tutorial-card__button--primary",
    "next"
  );

  actions.appendChild(exit);
  actions.appendChild(back);
  actions.appendChild(retry);
  actions.appendChild(next);
  card.appendChild(actions);

  return card;
}

function waitForStepAction(card, step, target, type, context = {}) {
  return new Promise((resolve) => {
    let finished = false;
    let advancing = false;
    const finish = (action) => {
      if (finished) return;
      finished = true;
      if (action === "abort" || (action === "next" && context.isLast)) {
        releaseTutorialDom();
      }
      resolve(action);
    };

    if (context.session) {
      context.session.cleanup.push(() => finish("abort"));
    }

    const advance = async (button) => {
      if (advancing) return;
      advancing = true;

      if (target && step.clickTarget !== false) {
        if (type === "textWaitInput" || step.waitForInput === true) {
          waitForInputTarget(target, button, finish);
          advancing = false;
          return;
        }

        await activateTarget(step, target, context.router);

        if (context.isLast || step.afterTargetClickDelayMs) {
          setTimeout(() => finish("next"), step.afterTargetClickDelayMs || 120);
          return;
        }
      }

      finish("next");
    };

    card.addEventListener("click", (event) => {
      const button = event.target.closest("[data-tutorial-action]");
      if (!button) return;

      const action = button.getAttribute("data-tutorial-action");
      if (action !== "next") {
        finish(action);
        return;
      }

      advance(button);
    });

    const overlay = document.getElementById("tutorial-overlay");
    if (overlay) {
      overlay.addEventListener("tutorialAction", (event) => {
        if (event instanceof CustomEvent) {
          const action = event.detail?.action || "next";
          if (action === "next" && event.detail?.origin === "highlight") {
            advance(null);
            return;
          }
          finish(action);
        }
      });
    }
  });
}

async function activateTarget(step, target, router) {
  if (step.expandMenuRoutePath) {
    notifyMenuRouteExpand(step.expandMenuRoutePath);
    return undefined;
  }

  if (step.navigateRoutePath && router) {
    try {
      await router.push(step.navigateRoutePath);
    } catch (error) {
      return undefined;
    }

    notifyMenuRouteSelected(step.navigateRoutePath);
    return undefined;
  }

  target.click();
  return undefined;
}

function notifyMenuRouteExpand(routePath) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("tutorial:expand-menu-route", {
      detail: { routePath },
    })
  );
}

function notifyMenuRouteSelected(routePath) {
  if (typeof window === "undefined") return;
  window.dispatchEvent(
    new CustomEvent("tutorial:menu-route-selected", {
      detail: { routePath },
    })
  );
}

function waitForInputTarget(target, button, finish) {
  target.click();
  if (typeof target.focus === "function") {
    target.focus();
  }

  if (button) {
    button.textContent = "Avancar";
    button.onclick = () => finish("next");
  }

  const done = () => finish("next");
  target.addEventListener("blur", done, { once: true });
  target.addEventListener(
    "keydown",
    (event) => {
      if (event.key === "Enter") done();
    },
    { once: true }
  );
}

function expandMenuNavigationStep(step, router) {
  if (!step || step.__tutorialMenuStep) return [];

  const context = resolveMenuNavigationContext(step, router);
  if (!context) return [];

  return [
    createMenuGroupStep(step, context),
    createMenuItemStep(step, context),
  ];
}

function prepareRuntimeSteps(steps, router) {
  let simulatedRoute = router?.currentRoute || {};

  return steps.flatMap((step) => {
    const context = resolveMenuNavigationContext(step, router, simulatedRoute);

    if (context) {
      simulatedRoute = resolveSimulatedRoute(
        simulatedRoute,
        step,
        router,
        context
      );
      return [
        createMenuGroupStep(step, context),
        createMenuItemStep(step, context),
      ];
    }

    simulatedRoute = resolveSimulatedRoute(simulatedRoute, step, router);
    return [step];
  });
}

async function renderMenuNavigationStep(step, index, total, session) {
  if (!step?.__tutorialMenuStep) return null;

  const context = {
    routeName: step.menuRouteName,
    routePath: step.menuRoutePath,
  };
  const target = await prepareMenuNavigationTarget(
    context,
    step.menuFocus,
    step
  );

  if (!target || session.aborted) {
    return showMissingTargetStep(step, index, total, session);
  }

  return renderVisibleStep(step, index, total, session, target, "text", false);
}

function resolveMenuNavigationContext(step, router, currentRoute = null) {
  if (
    !router ||
    step.navigationOnly !== true ||
    step.guideMenuNavigation === false
  ) {
    return null;
  }

  const routeName = step.routeName || step.route;
  const routePath = resolveRoutePath(
    router,
    routeName,
    step.routePath || step.path
  );
  const current = currentRoute || router.currentRoute || {};
  const shouldNavigateByName = routeName && current.name !== routeName;
  const shouldNavigateByPath =
    routePath && normalizePath(current.path) !== normalizePath(routePath);

  if (!routePath || (!shouldNavigateByName && !shouldNavigateByPath)) {
    return null;
  }

  return { routeName, routePath };
}

function resolveSimulatedRoute(currentRoute, step, router, context = null) {
  const routeName = context?.routeName || step.routeName || step.route;
  const routePath =
    context?.routePath ||
    resolveRoutePath(router, routeName, step.routePath || step.path);

  if (!routeName && !routePath) return currentRoute || {};

  return {
    ...(currentRoute || {}),
    name: routeName || currentRoute?.name,
    path: routePath || currentRoute?.path,
  };
}

async function prepareMenuNavigationTarget(context, focus, step) {
  if (focus === "item") {
    const visibleShortcut = findMenuShortcut(context.routePath, {
      requireViewport: true,
    });
    if (visibleShortcut) return visibleShortcut;
  }

  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent("tutorial:show-menu-route", {
        detail: {
          routeName: context.routeName,
          routePath: context.routePath,
          focus,
        },
      })
    );
  }

  const ready = await waitForMenuRouteReady(
    context.routePath,
    focus,
    step.menuTargetTimeoutMs || 3500
  ).catch(() => null);

  let target =
    focus === "group"
      ? findMenuGroupShortcut(ready?.groupTitle, { requireViewport: true })
      : findMenuShortcut(context.routePath, { requireViewport: true });

  if (!target) {
    const menuShortcut =
      focus === "group"
        ? findMenuGroupShortcut(ready?.groupTitle)
        : findMenuShortcut(context.routePath);
    if (menuShortcut) {
      scrollMenuShortcutIntoView(menuShortcut);
      await awaitTimeout(step.afterMenuScrollDelayMs || 260);
    }

    target =
      focus === "group"
        ? findMenuGroupShortcut(ready?.groupTitle, { requireViewport: true })
        : await waitForMenuShortcut(
            context.routePath,
            step.menuTargetTimeoutMs || 3000,
            { requireViewport: true }
          ).catch(() => null);
  }

  return target || null;
}

function createMenuGroupStep(step, context) {
  return {
    ...step,
    __tutorialMenuStep: true,
    menuFocus: "group",
    menuRouteName: context.routeName,
    menuRoutePath: context.routePath,
    type: "text",
    navigationOnly: false,
    route: null,
    routeName: null,
    routePath: null,
    path: null,
    selector: null,
    selectors: [],
    clickTarget: true,
    expandMenuRoutePath: context.routePath,
    boxTitle: step.menuGroupBoxTitle || "Abrir menu",
    boxText:
      step.menuGroupBoxText ||
      "Primeiro abra este menu para localizar a tela do processo.",
    buttonText: step.menuGroupButtonText || "Abrir menu",
    afterTargetClickDelayMs: step.afterMenuGroupClickDelayMs || 260,
  };
}

function createMenuItemStep(step, context) {
  return {
    ...step,
    __tutorialMenuStep: true,
    menuFocus: "item",
    menuRouteName: context.routeName,
    menuRoutePath: context.routePath,
    type: "text",
    navigationOnly: false,
    route: null,
    routeName: null,
    routePath: null,
    path: null,
    selector: null,
    selectors: [],
    clickTarget: step.menuClickTarget === false ? false : true,
    navigateRoutePath: context.routePath,
    boxTitle: step.menuBoxTitle || step.boxTitle || "Abrir tela",
    boxText:
      step.menuBoxText ||
      "Agora selecione esta tela no menu para continuar o tutorial.",
    buttonText: step.menuButtonText || step.buttonText || "Abrir tela",
    afterTargetClickDelayMs:
      step.afterTargetClickDelayMs || step.afterNavigationDelayMs || 450,
  };
}

async function navigateIfNeeded(step, router) {
  if (!router) return;

  const routeName = step.routeName || step.route;
  const routePath = resolveRoutePath(
    router,
    routeName,
    step.routePath || step.path
  );
  const current = router.currentRoute || {};
  const shouldNavigateByName = routeName && current.name !== routeName;
  const shouldNavigateByPath =
    routePath && normalizePath(current.path) !== normalizePath(routePath);

  if (!shouldNavigateByName && !shouldNavigateByPath) return;

  try {
    if (
      routePath &&
      step.preferMenuShortcut !== false &&
      clickMenuShortcut(routePath)
    ) {
      await awaitTimeout(step.afterShortcutClickDelayMs || 350);
    } else if (routeName) {
      await router.push({ name: routeName });
    } else if (routePath) {
      await router.push(routePath);
    }
  } catch (error) {
    // Vue Router rejects duplicated navigation. It is harmless for tutorials.
  }

  await awaitTimeout(step.afterNavigationDelayMs || 450);
}

function resolveRoutePath(router, routeName, routePath) {
  if (routePath) return routePath;
  if (!routeName || typeof router.resolve !== "function") return "";

  try {
    const resolved = router.resolve({ name: routeName });
    return resolved?.route?.path || resolved?.href || "";
  } catch (error) {
    return "";
  }
}

function clickMenuShortcut(routePath) {
  const target = findMenuShortcut(routePath);
  if (!target) return false;
  target.click();
  return true;
}

function findMenuShortcut(routePath, options = {}) {
  const targetPath = normalizePath(routePath);
  if (!targetPath) return null;

  const shortcuts = Array.from(
    document.querySelectorAll("[data-tutorial-menu-link]")
  );
  return (
    shortcuts.find((item) => {
      if (!(item instanceof HTMLElement) || item.offsetParent === null)
        return false;
      if (options.requireViewport && !isElementInViewport(item)) return false;
      return (
        normalizePath(item.getAttribute("data-tutorial-menu-link")) ===
        targetPath
      );
    }) || null
  );
}

function findMenuGroupShortcut(groupTitle, options = {}) {
  if (!groupTitle) return null;

  const groups = Array.from(
    document.querySelectorAll('[data-tutorial-menu-source="drawer-group"]')
  );
  const group = groups.find((item) => {
    if (!(item instanceof HTMLElement) || item.offsetParent === null) {
      return false;
    }

    const titleMatches =
      item.getAttribute("data-tutorial-menu-group-title") === groupTitle;
    if (!titleMatches) return false;

    const target = item.querySelector(".v-list-group__header") || item;
    return !options.requireViewport || isElementInViewport(target);
  });

  return group?.querySelector(".v-list-group__header") || group || null;
}

function waitForMenuShortcut(routePath, timeoutMs, options = {}) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const find = () => findMenuShortcut(routePath, options);

    const immediate = find();
    if (immediate) {
      resolve(immediate);
      return;
    }

    const interval = setInterval(() => {
      const target = find();
      if (target) {
        clearInterval(interval);
        resolve(target);
        return;
      }

      if (Date.now() - startedAt > timeoutMs) {
        clearInterval(interval);
        reject(new Error("Menu shortcut not found"));
      }
    }, 120);
  });
}

function waitForMenuRouteReady(routePath, focus, timeoutMs) {
  return new Promise((resolve, reject) => {
    if (typeof window === "undefined") {
      reject(new Error("Window not available"));
      return;
    }

    const timer = setTimeout(() => {
      window.removeEventListener("tutorial:menu-route-ready", onReady);
      reject(new Error("Menu route ready timeout"));
    }, timeoutMs);

    const onReady = (event) => {
      const eventRoutePath = event?.detail?.routePath || "";
      if (normalizePath(eventRoutePath) !== normalizePath(routePath)) return;
      if ((event?.detail?.focus || "item") !== focus) return;

      clearTimeout(timer);
      window.removeEventListener("tutorial:menu-route-ready", onReady);
      resolve(event.detail || {});
    };

    window.addEventListener("tutorial:menu-route-ready", onReady);
  });
}

function scrollMenuShortcutIntoView(target) {
  const container = findScrollableParent(target);
  if (container) {
    const containerRect = container.getBoundingClientRect();
    const targetRect = target.getBoundingClientRect();
    container.scrollTop +=
      targetRect.top -
      containerRect.top -
      container.clientHeight / 2 +
      targetRect.height / 2;
    return;
  }

  if (typeof target.scrollIntoView === "function") {
    target.scrollIntoView({
      behavior: "auto",
      block: "center",
      inline: "nearest",
    });
  }
}

function findScrollableParent(target) {
  let parent = target?.parentElement || null;
  while (parent && parent !== document.body) {
    const style = window.getComputedStyle(parent);
    const scrolls = /(auto|scroll)/.test(style.overflowY || "");
    if (scrolls && parent.scrollHeight > parent.clientHeight) {
      return parent;
    }
    parent = parent.parentElement;
  }

  return null;
}

function isElementInViewport(target) {
  const rect = target.getBoundingClientRect();
  const viewportWidth =
    window.innerWidth || document.documentElement.clientWidth;
  const viewportHeight =
    window.innerHeight || document.documentElement.clientHeight;

  return (
    rect.width > 0 &&
    rect.height > 0 &&
    rect.bottom > 0 &&
    rect.right > 0 &&
    rect.top < viewportHeight &&
    rect.left < viewportWidth
  );
}

function renderHighlight(overlay, target, step, session) {
  const highlight = document.createElement("div");
  highlight.className = "tutorial-highlight";
  keepPageFocus(highlight);
  overlay.appendChild(highlight);
  updateHighlight(overlay, target);

  highlight.addEventListener("click", () => {
    dispatchAction("next", null, "highlight");
  });

  session.cleanup.push(() => {
    highlight.remove();
  });
}

function updateHighlight(overlay, target) {
  const highlight = overlay.querySelector(".tutorial-highlight");
  if (!highlight || !target) return;

  const rect = target.getBoundingClientRect();
  highlight.style.left = `${Math.max(8, rect.left - HIGHLIGHT_PADDING)}px`;
  highlight.style.top = `${Math.max(8, rect.top - HIGHLIGHT_PADDING)}px`;
  highlight.style.width = `${Math.max(
    36,
    rect.width + HIGHLIGHT_PADDING * 2
  )}px`;
  highlight.style.height = `${Math.max(
    36,
    rect.height + HIGHLIGHT_PADDING * 2
  )}px`;
  highlight.style.borderRadius =
    rect.height > rect.width * 0.7 ? "999px" : "12px";
}

function positionInfoBox(card, target) {
  if (!target) {
    card.style.left = "50%";
    card.style.top = "50%";
    card.style.transform = "translate(-50%, -50%)";
    return;
  }

  const rect = target.getBoundingClientRect();
  const margin = 18;
  const cardWidth = Math.min(380, window.innerWidth - margin * 2);
  card.style.width = `${cardWidth}px`;
  // Altura real (o texto de cada passo varia): o cartão e os botões nunca saem da tela.
  const cardHeight = card.offsetHeight || 180;
  const spaceRight = window.innerWidth - rect.right;
  const left =
    spaceRight >= cardWidth + margin
      ? rect.right + margin
      : Math.max(margin, rect.left - cardWidth - margin);

  const below = rect.bottom + margin;
  const top =
    below + cardHeight + margin <= window.innerHeight
      ? below
      : Math.max(margin, rect.top - cardHeight - margin);
  const maxLeft = Math.max(margin, window.innerWidth - cardWidth - margin);
  const maxTop = Math.max(margin, window.innerHeight - cardHeight - margin);

  card.style.left = `${Math.min(left, maxLeft)}px`;
  card.style.top = `${Math.min(top, maxTop)}px`;
  card.style.transform = "none";
}

// Clicar no cartão ou no destaque não tira o foco da página: com um v-dialog aberto, o foco
// fora dele faz o Vuetify focar o primeiro campo e rolar o formulário até o topo.
function keepPageFocus(element) {
  element.addEventListener("mousedown", (event) => event.preventDefault());
}

function primaryLabel(step, context = {}) {
  if (step.buttonText) return step.buttonText;
  if (!context.target || step.clickTarget === false || context.missingTarget) {
    return context.isLast ? "Concluir" : "Avancar";
  }

  const type = context.type || step.type;
  if (type === "textWaitInput" || step.waitForInput === true)
    return "Preencher";
  return context.isLast ? "Executar e concluir" : "Executar";
}

function createButton(label, className, action) {
  const button = document.createElement("button");
  button.type = "button";
  button.className = className;
  button.textContent = label;
  button.setAttribute("data-tutorial-action", action);
  return button;
}

function normalizeSteps(rawSteps) {
  if (typeof rawSteps === "string") {
    try {
      rawSteps = JSON.parse(rawSteps);
    } catch (error) {
      return [];
    }
  }

  if (!Array.isArray(rawSteps)) return [];
  return rawSteps
    .filter((step) => step && typeof step === "object")
    .map((step) => {
      const selectors = normalizeSelectorList(step.selectors, step.selector);

      return {
        ...step,
        selectors,
        type: step.type || (selectors.length ? "text" : "textOnly"),
        clickTarget: step.clickTarget === undefined ? true : step.clickTarget,
        timeoutMs: Number(step.timeoutMs || DEFAULT_TIMEOUT),
      };
    });
}

function resolveStepDisplayPosition(steps, currentIndex) {
  const total = steps.filter(isVisibleCounterStep).length || steps.length;
  const index = Math.max(
    0,
    steps.slice(0, currentIndex + 1).filter(isVisibleCounterStep).length - 1
  );

  return { index, total };
}

function previousVisibleStepIndex(steps, currentIndex) {
  for (let index = currentIndex - 1; index >= 0; index -= 1) {
    if (isVisibleCounterStep(steps[index])) {
      return index;
    }
  }

  return 0;
}

function isVisibleCounterStep(step) {
  return !isHiddenNavigationStep(step);
}

function isHiddenNavigationStep(step) {
  return (
    step?.navigationOnly === true &&
    getStepSelectors(step).length === 0 &&
    step.countStep !== true
  );
}

function getStepSelectors(step) {
  return normalizeSelectorList(step?.selectors, step?.selector);
}

function normalizeSelectorList(selectors, fallbackSelector = "") {
  const rawSelectors = Array.isArray(selectors) ? selectors : [selectors];
  const normalized = rawSelectors
    .concat(fallbackSelector)
    .filter((selector) => typeof selector === "string")
    .map((selector) => selector.trim())
    .filter(Boolean);

  const uniqueSelectors = normalized.filter((selector, index, list) => {
    return list.indexOf(selector) === index;
  });

  return uniqueSelectors.reduce((list, selector) => {
    const aliases = SELECTOR_ALIASES[selector] || [];
    return [...list, selector, ...aliases].filter((item, index, items) => {
      return items.indexOf(item) === index;
    });
  }, []);
}

function sanitizeHtml(value) {
  return String(value || "")
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son[a-z]+\s*=/gi, " data-disabled-event=");
}

function getTarget(selectors, timeoutMs = DEFAULT_TIMEOUT) {
  return new Promise((resolve, reject) => {
    const startedAt = Date.now();
    const find = () => findTarget(selectors);

    const immediate = find();
    if (immediate) {
      resolve(immediate);
      return;
    }

    const interval = setInterval(() => {
      const target = find();
      if (target) {
        clearInterval(interval);
        resolve(target);
        return;
      }

      if (Date.now() - startedAt > timeoutMs) {
        clearInterval(interval);
        reject(new Error("Target not found"));
      }
    }, 180);
  });
}

function findTarget(selectors) {
  return normalizeSelectorList(selectors).reduce((found, selector) => {
    if (found) return found;

    try {
      const matches = Array.from(document.querySelectorAll(selector));
      return (
        matches.find(
          (item) => item instanceof HTMLElement && item.offsetParent !== null
        ) || null
      );
    } catch (error) {
      return null;
    }
  }, null);
}

function scrollIntoView(element) {
  return new Promise((resolve) => {
    try {
      element.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "center",
      });
      setTimeout(resolve, 260);
    } catch (error) {
      resolve();
    }
  });
}

function createBlockOverlay() {
  const overlay = ensureBlockOverlay();
  overlay.innerHTML = "";
}

function ensureBlockOverlay() {
  let overlay = document.getElementById("tutorial-overlay-block");
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = "tutorial-overlay-block";
    document.body.appendChild(overlay);
  }
  return overlay;
}

function destroyBlockOverlay() {
  document
    .querySelectorAll("#tutorial-overlay-block")
    .forEach((blockOverlay) => {
      blockOverlay.remove();
    });
}

function clearFrame() {
  document.querySelectorAll(TUTORIAL_FRAME_SELECTOR).forEach((element) => {
    element.remove();
  });
}

function releaseTutorialDom(scheduleFollowUp = true) {
  const releaseToken = scheduleFollowUp
    ? ++releaseGeneration
    : releaseGeneration;

  disableTutorialPointerEvents();
  releaseTutorialFocus();
  clearFrame();
  destroyBlockOverlay();

  if (!scheduleFollowUp) return;

  [60, 180, 360].forEach((delay) => {
    setTimeout(() => {
      if (activeSession || releaseGeneration !== releaseToken) return;
      releaseTutorialDom(false);
    }, delay);
  });
}

function disableTutorialPointerEvents() {
  document.querySelectorAll(TUTORIAL_DOM_SELECTOR).forEach((element) => {
    if (element instanceof HTMLElement) {
      element.style.pointerEvents = "none";
    }
  });
}

function releaseTutorialFocus() {
  const activeElement = document.activeElement;
  if (!(activeElement instanceof HTMLElement)) return;
  if (!activeElement.closest(TUTORIAL_DOM_SELECTOR)) return;

  activeElement.blur();
}

function runCleanup(session) {
  if (!session || !Array.isArray(session.cleanup)) return;
  session.cleanup.splice(0).forEach((fn) => {
    try {
      fn();
    } catch (error) {
      return undefined;
    }
  });
}

function awaitTimeout(timeout) {
  return new Promise((resolve) => setTimeout(resolve, timeout));
}

function normalizePath(value) {
  return String(value || "")
    .split("?")[0]
    .split("#")[0]
    .replace(/\/+$/, "")
    .toLowerCase();
}

export { helpers };
